import { BigDecimal } from "@solomon-labs/bigdecimal";

/**
 * Convert a token amount (number) to raw blockchain units
 *
 * @param token - Token amount as number (e.g., 1.5)
 * @param decimals - Token decimals (e.g., 6 for USDC)
 * @returns Raw amount as BigDecimal
 *
 * @example
 * convertTokenToRaw(1.5, 6)
 * // Returns: BigDecimal(1500000)
 */
export function convertTokenToRaw(token: number, decimals: number): BigDecimal {
    return new BigDecimal(token).multiply(BigDecimal.pow10(decimals));
}

/**
 * Convert a token amount string to raw blockchain units
 * Handles edge cases like commas, empty strings, and decimal truncation
 *
 * @param token - Token amount as string (e.g., "1,234.56")
 * @param decimals - Token decimals
 * @returns Raw amount as BigDecimal
 *
 * @example
 * convertStringtoRaw("1,234.56", 6)
 * // Returns: BigDecimal(1234560000)
 *
 * convertStringtoRaw("", 6)
 * // Returns: BigDecimal(0)
 *
 * convertStringtoRaw("0.123", 2)  // Truncates to 2 decimals
 * // Returns: BigDecimal(12)
 */
export function convertStringtoRaw(token: string, decimals: number): BigDecimal {
    if (!token) return new BigDecimal(0);

    const sanitized = token.trim().replace(/,/g, "");
    if (sanitized === "" || sanitized === ".") return new BigDecimal(0);

    const [integerPartRaw, decimalPartRaw = ""] = sanitized.split(".");
    const integerPart = integerPartRaw.replace(/^0+(?=\d)/, "") || "0";

    // Pad/truncate decimals to the token's precision
    const paddedDecimals = (decimalPartRaw + "0".repeat(decimals)).slice(0, decimals);

    const rawString = `${integerPart}${paddedDecimals}`.replace(/^0+(?=\d)/, "") || "0";
    return new BigDecimal(rawString);
}

/**
 * Convert raw blockchain units to human-readable token amount
 *
 * @param raw - Raw amount as BigDecimal
 * @param decimals - Token decimals
 * @returns Human-readable amount as string
 *
 * @example
 * convertRawToToken(new BigDecimal(1500000), 6)
 * // Returns: "1.5"
 *
 * convertRawToToken(new BigDecimal(0), 9)
 * // Returns: "0"
 */
export function convertRawToToken(raw: BigDecimal, decimals: number): string {
    if (!raw) return "0";
    return raw.divide(BigDecimal.pow10(decimals)).toString();
}

/**
 * Convert human readable amount to raw token amount
 *
 * @param amount - Human readable amount (e.g., 100.5)
 * @param decimals - Token decimals
 * @returns Raw amount as bigint
 */
export function toRawAmount(amount: BigDecimal, decimals: number): bigint {
    return amount.multiply(BigDecimal.pow10(decimals)).toBigint();
}

/**
 * Convert raw token amount to human readable
 *
 * @param rawAmount - Raw amount as bigint
 * @param decimals - Token decimals
 * @returns Human readable BigDecimal
 */
export function fromRawAmount(rawAmount: bigint, decimals: number): BigDecimal {
    return new BigDecimal(rawAmount).divide(BigDecimal.pow10(decimals));
}
